// Connects the signed-in session to the shared SSE leader (lib/events.ts) and turns events into cache
// invalidations. SSE is only an invalidation channel (§8): content is always re-fetched through REST.
import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useSession, ME_KEY } from '../lib/session';
import { startEvents, type HubEvent } from '../lib/events';
import { announce } from '../lib/announce';
import { rebindIfOwned } from '../lib/push';
import { probeIdentity } from '../lib/api';
import { freezeImpersonation, impersonationOf, onSessionChanged, sessionTagOf } from '../lib/impersonation';

const mocked = () => import.meta.env.DEV && (import.meta.env.VITE_MOCK || new URLSearchParams(location.search).has('mockAs') || sessionStorage.getItem('wd-mock') === '1');
const MESSAGE_TOPICS = new Set(['message.created', 'thread.read']);
const NOTIFICATION_TOPICS = new Set(['notification.created', 'notification.read']);

export function EventsBridge() {
  const { me } = useSession();
  const qc = useQueryClient();
  const nav = useNavigate();
  const uid = me?.id;
  const tag = sessionTagOf(me);
  const acting = !!impersonationOf(me);

  useEffect(() => {
    if (!uid || mocked()) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let all = false;
    const keys = new Set<string>();
    const flush = () => {
      timer = undefined;
      if (all) void qc.invalidateQueries();
      else for (const k of keys) void qc.invalidateQueries({ queryKey: [k] });
      all = false;
      keys.clear();
    };
    const queue = (k?: string) => {
      if (k) keys.add(k);
      else all = true;
      timer ??= setTimeout(flush, 120);
    };
    const startedAt = Date.now();
    let firstReady = true;
    const onEvent = (e: HubEvent) => {
      if (e.name === 'ready' || e.name === 'reset') {
        // The very first "ready" after page load would only repeat the fetches the page just made.
        if (e.name === 'ready' && firstReady && Date.now() - startedAt < 4000) {
          firstReady = false;
          queue('notifications');
          return;
        }
        firstReady = false;
        queue();
        return;
      }
      if (e.name === 'closed') {
        // The stream was refused (401 after logout elsewhere, or a server restart): re-check the session.
        void qc.invalidateQueries({ queryKey: ME_KEY });
        return;
      }
      if (e.name !== 'update') return;
      const topic: string = e.data?.topic ?? '';
      if (topic === 'access_changed') {
        void qc.invalidateQueries({ queryKey: ME_KEY });
        queue();
      } else if (NOTIFICATION_TOPICS.has(topic)) {
        queue('notifications');
        if (topic === 'notification.created' && document.visibilityState === 'visible') announce('New notification');
      } else if (MESSAGE_TOPICS.has(topic)) {
        queue('messages');
        queue('threads');
      } else queue();
    };
    // Keyed on id:sessionTag (§2h): a new session (start, exit) restarts the hub with the new tag.
    const stop = startEvents(uid, onEvent, undefined, undefined, {
      tag,
      probe: probeIdentity,
      onChanged: onSessionChanged,
      onUnauthorized: () => (acting ? freezeImpersonation() : void qc.invalidateQueries({ queryKey: ME_KEY })),
    });
    // Push belongs to the real account holder's device: never rebind while acting as a client.
    if (!acting) void rebindIfOwned(uid);
    return () => {
      stop();
      clearTimeout(timer);
    };
  }, [uid, tag, acting, qc]);

  // A notification click while the app is already open: the worker asks us to navigate (no reload).
  useEffect(() => {
    if (!uid || !('serviceWorker' in navigator)) return;
    const onMessage = (e: MessageEvent) => {
      const d = e.data as { type?: string; link?: unknown } | null;
      if (d?.type === 'navigate' && typeof d.link === 'string' && d.link.startsWith('/') && !d.link.startsWith('//')) nav(d.link);
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, [uid, nav]);

  return null;
}
